import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API || "https://www.vilaro.ro/api";

const inputStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "white", outline: "none" };
const btnGray = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" };
const btnPrimary = { background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "white" };

const classificationStyles = {
  sustinator: { background: "rgba(34,197,94,0.15)", color: "#86efac" },
  critic: { background: "rgba(234,179,8,0.15)", color: "#fde047" },
  troll: { background: "rgba(239,68,68,0.15)", color: "#fca5a5" },
  intrebare: { background: "rgba(99,102,241,0.15)", color: "#a5b4fc" },
  neutru: { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" },
};

const borderForClass = (cls) =>
  cls === 'troll' ? '#fca5a5' : cls === 'critic' ? '#fde047' :
  cls === 'sustinator' ? '#86efac' : cls === 'intrebare' ? '#a5b4fc' : 'rgba(255,255,255,0.15)';

const buildFbPermalink = (postId, commentId, parentId) => {
  if (!postId || typeof postId !== 'string' || !postId.includes('_')) return null;
  const parts = postId.split('_');
  if (parts.length < 2 || !parts[0] || !parts[1]) return null;
  const targetId = (parentId && typeof parentId === 'string' && parentId.includes('_')) ? parentId : commentId;
  const cid = (targetId && targetId.includes('_')) ? targetId.split('_')[1] : targetId;
  return `https://www.facebook.com/permalink.php?story_fbid=${parts[1]}&id=${parts[0]}&comment_id=${cid}`;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const normalized = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
  const mins = Math.floor((Date.now() - new Date(normalized).getTime()) / 60000);
  if (mins < 1) return "acum";
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}z`;
};

const ageHours = (dateStr) => {
  if (!dateStr) return 0;
  const normalized = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
  return (Date.now() - new Date(normalized).getTime()) / 3600000;
};

const toastStyles = `
@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
`;

export default function ModeratorInbox() {
  const [pages, setPages] = useState([]);
  const [section, setSection] = useState("active"); // active | ascunse
  const [filterAccount, setFilterAccount] = useState("");
  const [filterPage, setFilterPage] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ active: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const perPage = 50;
  const [sla, setSla] = useState({ sla_yellow_hours: 1, sla_red_hours: 3 });
  const [toasts, setToasts] = useState([]);

  const searchTimeoutRef = useRef(null);
  const pollingRef = useRef(null);
  const notifiedRef = useRef(new Set());
  const abortRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/pages`).then(res => setPages(res.data.pages)).catch(() => {});
    axios.get(`${API}/settings/sla`).then(res => setSla(res.data)).catch(() => {});
  }, []);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880; osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  };

  const slaStatus = useCallback((dateStr) => {
    const hrs = ageHours(dateStr);
    if (hrs >= sla.sla_red_hours) return "red";
    if (hrs >= sla.sla_yellow_hours) return "yellow";
    return "green";
  }, [sla]);

  const fetchData = useCallback(async (isPolling = false) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const tab = section === "active" ? "moderator_active" : "moderator_hidden";
    const params = new URLSearchParams({ tab, filter, limit: perPage, offset: page * perPage });
    if (search && search.trim()) params.append("search", search.trim());
    if (filterPage) params.append("page_id", filterPage);
    else if (filterAccount) params.append("account_id", filterAccount);
    try {
      const res = await axios.get(`${API}/comments/inbox?${params}`, { signal: abortRef.current.signal });
      setComments(res.data.comments || []);
      setTotal(res.data.total || 0);

      // Alerta SLA depasit (rosu) — beep + toast pentru cele noi
      let redNew = 0;
      (res.data.comments || []).forEach(c => {
        if (slaStatus(c.created_time) === "red" && !notifiedRef.current.has(c.id)) {
          redNew++; notifiedRef.current.add(c.id);
        }
      });
      if (redNew > 0 && isPolling) {
        playBeep(); setTimeout(playBeep, 300);
        const id = Date.now();
        setToasts(prev => [...prev, { id, n: redNew }]);
      }
    } catch (e) {
      if (axios.isCancel(e) || e.name === 'CanceledError') return;
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [section, filter, search, page, filterAccount, filterPage, slaStatus]);

  const [unhidden, setUnhidden] = useState({});
  const [unhiding, setUnhiding] = useState({});

  const doUnhide = async (c) => {
    setUnhiding(prev => ({ ...prev, [c.id]: true }));
    try {
      await axios.post(`${API}/comments/${c.id}/unhide?page_id=${c.page_id}`);
      setUnhidden(prev => ({ ...prev, [c.id]: true }));
    } catch (e) {
      console.error("Eroare afisare:", e);
      alert("Nu am putut afișa comentariul. Încearcă din nou.");
    } finally {
      setUnhiding(prev => ({ ...prev, [c.id]: false }));
    }
  };

  const fetchCounts = useCallback(async () => {
    try {
      const a = await axios.get(`${API}/comments/inbox?tab=moderator_active&limit=1`);
      const h = await axios.get(`${API}/comments/inbox?tab=moderator_hidden&limit=1`);
      setCounts({ active: a.data.total || 0, hidden: h.data.total || 0 });
    } catch (e) {}
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData();
    fetchCounts();
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => { fetchData(true); fetchCounts(); }, 30000);
    return () => clearInterval(pollingRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, filter, search, page, filterAccount, filterPage]);

  const handleSearch = (val) => {
    setSearchInput(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { setPage(0); setSearch(val); }, 400);
  };

  // Sortare automata: rosu -> galben -> verde, apoi cronologic desc
  const sorted = useMemo(() => {
    const rank = { red: 0, yellow: 1, green: 2 };
    return [...comments].sort((a, b) => {
      const ra = rank[slaStatus(a.created_time)], rb = rank[slaStatus(b.created_time)];
      if (ra !== rb) return ra - rb;
      return new Date(b.created_time) - new Date(a.created_time);
    });
  }, [comments, slaStatus]);

  const redCount = useMemo(() => comments.filter(c => slaStatus(c.created_time) === "red").length, [comments, slaStatus]);

  const accounts = useMemo(() => [...new Map(pages.filter(p => p.account_id).map(p => [p.account_id, { id: p.account_id, name: p.account_name || `Cont ${p.account_id}` }])).values()], [pages]);

  const classFilters = [
    { id: "all", label: "Toate" },
    { id: "sustinator", label: "Susținători" },
    { id: "critic", label: "Critici" },
    { id: "troll", label: "Troli" },
    { id: "intrebare", label: "Întrebări" },
    { id: "neutru", label: "Neutri" },
  ];

  return (
    <div>
      <style>{toastStyles}</style>

      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Moderator</h2>
        <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>🕒 ultimele 48h</span>
      </div>
      <p className="text-sm mb-5" style={{ color: "rgba(255,255,255,0.35)" }}>
        Comentarii la care poți răspunde — sari pe Facebook și comentează de pe contul tău
      </p>

      {redCount > 0 && (
        <div className="rounded-xl px-4 py-3 mb-4 flex items-center gap-2"
          style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)" }}>
          <span style={{ color: "#fca5a5" }}>⚠️</span>
          <span className="text-sm" style={{ color: "#fca5a5" }}>
            {redCount} comentari{redCount === 1 ? 'u depășit' : 'i depășite'} — peste {sla.sla_red_hours}h fără răspuns
          </span>
        </div>
      )}

      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        {[{ id: "active", label: "Active", n: counts.active }, { id: "ascunse", label: "Ascunse", n: counts.hidden }].map(s => (
          <button key={s.id} onClick={() => { setSection(s.id); setPage(0); setFilter("all"); }}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all"
            style={section === s.id ? btnPrimary : { color: "rgba(255,255,255,0.4)" }}>
            {s.label} <span style={{ opacity: 0.6 }}>{s.n}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div>
          <p className="text-xs mb-1 font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>Conturi</p>
          <div className="flex gap-2">
            <button onClick={() => { setFilterAccount(""); setFilterPage(""); setPage(0); }}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={!filterAccount ? btnPrimary : btnGray}>Toate</button>
            {accounts.map(a => (
              <button key={a.id} onClick={() => { setFilterAccount(String(a.id)); setFilterPage(""); setPage(0); }}
                className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
                style={String(filterAccount) === String(a.id) ? btnPrimary : btnGray}>{a.name}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs mb-1 font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>Pagini</p>
          <select className="rounded-xl px-3 py-2 text-sm" style={inputStyle}
            value={filterPage} onChange={e => { setFilterPage(e.target.value); setPage(0); }}>
            <option value="" style={{ color: "black" }}>Toate paginile</option>
            {pages.filter(p => !filterAccount || String(p.account_id) === String(filterAccount)).map(p => (
              <option key={p.id} value={p.id} style={{ color: "black" }}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {classFilters.map(f => (
          <button key={f.id} onClick={() => { setFilter(f.id); setPage(0); }}
            className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
            style={filter === f.id ? btnPrimary : btnGray}>{f.label}</button>
        ))}
      </div>

      <div className="mb-4">
        <input type="text" placeholder="🔍 Caută după nume sau text comentariu..."
          className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle}
          value={searchInput} onChange={e => handleSearch(e.target.value)} />
      </div>

      <div className="flex items-center mb-3">
        <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
          {total === 0 ? "0 comentarii" : `${page * perPage + 1}-${Math.min((page + 1) * perPage, total)} din ${total}`}
        </span>
        <span className="text-xs ml-auto" style={{ color: "rgba(99,102,241,0.4)" }}>refresh la 30s</span>
      </div>

      {loading && <div className="text-sm py-8 text-center" style={{ color: "rgba(255,255,255,0.25)" }}>Se incarca...</div>}

      <div className="space-y-3">
        {!loading && sorted.map(c => {
          const fbLink = buildFbPermalink(c.post_id, c.id, c.parent_id);
          const st = slaStatus(c.created_time);
          const slaBadge = st === "red" ? { e: "🔴", bg: "rgba(239,68,68,0.2)", col: "#fca5a5" }
            : st === "yellow" ? { e: "🟡", bg: "rgba(234,179,8,0.2)", col: "#fde047" }
            : { e: "🟢", bg: "rgba(34,197,94,0.2)", col: "#86efac" };
          return (
            <div key={c.id} className="rounded-2xl p-4"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderLeft: `3px solid ${borderForClass(c.classification)}` }}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white">{c.from_name}</span>
                  {c.classification && <span className="text-xs px-2 py-0.5 rounded-full" style={classificationStyles[c.classification] || classificationStyles.neutru}>{c.classification}</span>}
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{c.page_name}</span>
                  {c.is_hidden === 1 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>ascuns</span>}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0" style={{ background: slaBadge.bg, color: slaBadge.col }}>
                  {slaBadge.e} {timeAgo(c.created_time)}
                </span>
              </div>

              {c.post_message && (
                <div className="rounded-lg px-3 py-1.5 mb-2 text-xs" style={{ background: "rgba(99,102,241,0.06)", borderLeft: "2px solid rgba(99,102,241,0.3)" }}>
                  <span style={{ color: "rgba(255,255,255,0.3)" }}>📄 </span>
                  <span style={{ color: "rgba(255,255,255,0.4)" }}>{c.post_message.substring(0, 80)}...</span>
                </div>
              )}

              {c.parent_chain && c.parent_chain.length > 0 && (
                <div className="space-y-1 mb-2">
                  {c.parent_chain.map((p, i) => (
                    <div key={p.id} className="rounded-lg px-3 py-1.5"
                      style={{ background: "rgba(255,255,255,0.02)", borderLeft: "2px solid rgba(255,255,255,0.08)", marginLeft: `${Math.min(i * 16, 48)}px` }}>
                      <span className="text-xs font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>
                        💬 <span style={{ color: "rgba(255,255,255,0.7)" }}>{p.from_name}</span>
                        {p.classification && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={classificationStyles[p.classification] || classificationStyles.neutru}>{p.classification}</span>}:
                      </span>
                      <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>{p.message}</p>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-sm mb-3" style={{ color: "rgba(255,255,255,0.85)", marginLeft: c.parent_chain && c.parent_chain.length > 0 ? `${Math.min(c.parent_chain.length * 16, 48)}px` : "0px" }}>{c.message}</p>

              {fbLink && section === "ascunse" && !unhidden[c.id] && (
                <button onClick={() => doUnhide(c)} disabled={unhiding[c.id]}
                  className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-medium disabled:opacity-50"
                  style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)", color: "#86efac", cursor: "pointer" }}>
                  {unhiding[c.id] ? "⏳ Se afișează..." : "📘 Afișează comentariul"}
                </button>
              )}
              {fbLink && (section !== "ascunse" || unhidden[c.id]) && (
                <a href={fbLink} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-medium"
                  style={{ background: unhidden[c.id] ? "rgba(34,197,94,0.15)" : "rgba(59,130,246,0.12)", border: unhidden[c.id] ? "1px solid rgba(34,197,94,0.4)" : "1px solid rgba(59,130,246,0.35)", color: unhidden[c.id] ? "#86efac" : "#93c5fd", textDecoration: "none" }}>
                  {unhidden[c.id] ? "✅ Afișat — Răspunde pe Facebook ↗" : "📘 Răspunde pe Facebook ↗"}
                </a>
              )}
            </div>
          );
        })}
        {!loading && sorted.length === 0 && (
          <div className="text-center py-16 text-sm" style={{ color: "rgba(255,255,255,0.2)" }}>
            {section === "ascunse" ? "Niciun comentariu ascuns în ultimele 48h" : "🎉 Niciun comentariu activ în ultimele 48h"}
          </div>
        )}
      </div>

      {total > perPage && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-30" style={btnGray}>← Anterior</button>
          <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>{page + 1} / {Math.ceil(total / perPage)}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * perPage >= total}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-30" style={btnGray}>Următor →</button>
        </div>
      )}

      <div className="fixed bottom-6 right-6 z-50 space-y-3" style={{ maxWidth: "320px" }}>
        {toasts.map(t => (
          <div key={t.id} className="rounded-2xl p-4 shadow-2xl"
            style={{ background: "linear-gradient(135deg,rgba(239,68,68,0.15),rgba(239,68,68,0.05))", border: "1px solid rgba(239,68,68,0.4)", backdropFilter: "blur(20px)", animation: "slideIn 0.3s ease" }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white mb-0.5">🔴 {t.n} comentari{t.n === 1 ? 'u depășit' : 'i depășite'}</p>
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>Peste {sla.sla_red_hours}h fără răspuns</p>
              </div>
              <button onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                className="text-xs px-2 py-1 rounded-lg flex-shrink-0" style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}>✕</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
